import React, { useEffect, useState } from 'react';
import { Check, Link2, Monitor, Share2, X } from 'lucide-react';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { shareOrCopyLink, type ShareOutcome } from '../../utils/shareLink';

/**
 * THE DESKTOP WALL — shown when someone on a handheld reaches for sign-in (RF3-DEVICE, owner 2026-08-22).
 *
 * It gives a REASON, not a rejection. "Unsupported device" reads as broken, which is the exact feeling this
 * exists to prevent; so it names what creation actually needs (a canvas, a timeline, the crew alongside) and
 * says plainly that viewing finished work on a phone is coming.
 *
 * It never traps anyone: a share/copy action carries the intent to a computer without inventing an
 * email-capture backend, and "Continue anyway" signs in regardless — a media query is a guess, and being
 * locked out of your own account is worse than a cramped layout.
 *
 * Presentational: props in, callbacks out. Whoever opens it owns the analytics (useDesktopSignInWall).
 */
interface Props {
  open: boolean;
  /** The escape hatch — proceed to sign-in anyway. */
  onContinueAnyway: () => void;
  /** Closed without continuing: the X, the backdrop, or Escape. */
  onDismiss: () => void;
  /** The link to hand to the visitor's computer. Defaults to wherever they are standing. */
  shareUrl?: string;
}

/** What to say after the share attempt. A native share needs no confirmation — the OS already gave one. */
const SHARE_STATUS: Partial<Record<ShareOutcome, string>> = {
  copied: 'Link copied. Paste it into your computer.',
  failed: 'Copy this link and open it on your computer:',
};

const DesktopOnlyWall: React.FC<Props> = ({ open, onContinueAnyway, onDismiss, shareUrl }) => {
  const trapRef = useFocusTrap<HTMLDivElement>(open);
  const [outcome, setOutcome] = useState<ShareOutcome | null>(null);
  const link = shareUrl || (typeof window !== 'undefined' ? window.location.href : '');

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onDismiss(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onDismiss]);

  // A fresh open must not still be showing the last attempt's confirmation.
  useEffect(() => { if (!open) setOutcome(null); }, [open]);

  if (!open) return null;

  const sendToComputer = async () => {
    setOutcome(await shareOrCopyLink(link, {
      title: 'Wisetree AI',
      text: 'Open Wisetree AI on your computer to start creating.',
    }));
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-black/60 p-4 sm:items-center motion-safe:animate-fade-in"
      onClick={onDismiss}
    >
      <div
        ref={trapRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="desktop-wall-title"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm rounded-2xl border border-secondary-200 bg-white p-6 shadow-xl motion-safe:animate-slide-in dark:border-secondary-700 dark:bg-secondary-900"
      >
        <div className="flex items-start justify-between gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-secondary-900 text-white dark:bg-white dark:text-secondary-900">
            <Monitor className="h-5 w-5" />
          </span>
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Close"
            className="-mr-2 -mt-2 rounded-lg p-2 text-secondary-400 transition-colors hover:text-secondary-900 focus:outline-none focus:ring-2 focus:ring-secondary-500 dark:hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <h2 id="desktop-wall-title" className="mt-4 text-lg font-semibold text-secondary-900 dark:text-white">
          Your studio needs a bigger screen
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-secondary-600 dark:text-secondary-300">
          Making a film here means a shot canvas, a timeline and your crew open beside each other. That is a
          desk-sized workspace — on a phone we would have to hide most of it, and you would be directing blind.
        </p>
        <p className="mt-2 text-sm leading-relaxed text-secondary-500 dark:text-secondary-400">
          Watching is a different job. Browsing, previewing and sharing your finished films from your phone is
          on the way.
        </p>

        <div className="mt-5 space-y-3">
          <button
            type="button"
            onClick={sendToComputer}
            className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-secondary-900 px-4 text-sm font-medium text-white transition-colors hover:bg-black focus:outline-none focus:ring-2 focus:ring-secondary-500 dark:bg-white dark:text-secondary-900 dark:hover:bg-secondary-100"
          >
            {outcome === 'copied' ? <Check className="h-4 w-4" /> : <Share2 className="h-4 w-4" />}
            Send this to my computer
          </button>

          {outcome && SHARE_STATUS[outcome] && (
            <p className="text-xs text-secondary-500 dark:text-secondary-400">{SHARE_STATUS[outcome]}</p>
          )}
          {/* Neither the share sheet nor the clipboard was available — show the link so it can be copied by hand. */}
          {outcome === 'failed' && (
            <div className="flex items-center gap-2 rounded-lg border border-secondary-200 bg-secondary-50 px-3 py-2 dark:border-secondary-700 dark:bg-secondary-800">
              <Link2 className="h-4 w-4 shrink-0 text-secondary-400" />
              <span className="truncate text-xs text-secondary-700 dark:text-secondary-200" title={link}>{link}</span>
            </div>
          )}

          <button
            type="button"
            onClick={onContinueAnyway}
            className="min-h-[44px] w-full rounded-xl border border-secondary-200 px-4 text-sm font-medium text-secondary-600 transition-colors hover:border-secondary-400 hover:text-secondary-900 focus:outline-none focus:ring-2 focus:ring-secondary-500 dark:border-secondary-700 dark:text-secondary-400 dark:hover:text-white"
          >
            Continue anyway
          </button>
        </div>
      </div>
    </div>
  );
};

export default DesktopOnlyWall;
